import { defineStore } from 'pinia'

import {
  descriptorPreferenceKeys,
  mergePreferenceGroup,
  orderDescriptors,
  type ColumnDescriptor,
} from '@/utils/columnLayout'
import {
  createColumnPreferenceStorage,
  parseColumnWidths,
  uniqueStringArray,
  type ColumnPreferenceWriteResult,
} from '@/utils/columnPreferenceStorage'

const STORAGE_PREFIX = 'moria:review-assay-columns:v1'

export type AssayColumnDescriptor = ColumnDescriptor

export interface AssayColumnPreference {
  order: string[]
  fixed: string[]
  hidden: string[]
  widths?: Record<string, number>
}

export type AssayColumnPreferenceCommand =
  | { type: 'save'; order: string[]; fixed: string[]; hidden: string[] }
  | { type: 'reset' }

export type AssayColumnPreferenceWriteResult = ColumnPreferenceWriteResult<AssayColumnPreference>

export interface ReconciledAssayColumnLayout {
  columns: AssayColumnDescriptor[]
  hiddenColumns: AssayColumnDescriptor[]
  fixedKeys: string[]
  hiddenKeys: string[]
  fixedCount: number
}

function parsePreference(raw: string): AssayColumnPreference | null {
  try {
    const candidate = JSON.parse(raw) as Partial<AssayColumnPreference>
    const order = uniqueStringArray(candidate.order)
    const fixed = uniqueStringArray(candidate.fixed)
    const hidden = uniqueStringArray(candidate.hidden)
    const widths = parseColumnWidths(candidate.widths)
    if (!order || !fixed || !hidden || !widths) return null
    const orderKeys = new Set(order)
    if (fixed.some((key) => !orderKeys.has(key)) || hidden.some((key) => !orderKeys.has(key))) {
      return null
    }
    const fixedKeys = new Set(fixed)
    if (hidden.some((key) => fixedKeys.has(key))) return null
    return { order, fixed, hidden, widths }
  } catch {
    return null
  }
}

function clonePreference(preference: AssayColumnPreference | null): AssayColumnPreference | null {
  return preference
    ? {
        order: [...preference.order],
        fixed: [...preference.fixed],
        hidden: [...preference.hidden],
        widths: { ...preference.widths },
      }
    : null
}

function preferenceIncludes(keys: Set<string>, descriptor: AssayColumnDescriptor): boolean {
  return descriptorPreferenceKeys(descriptor).some((key) => keys.has(key))
}

function assayColumnGroup(
  descriptor: AssayColumnDescriptor,
  hasPreference: boolean,
  orderedSet: Set<string>,
  fixedSet: Set<string>,
  hiddenSet: Set<string>,
): 'fixed' | 'scrollable' | 'hidden' {
  if (preferenceIncludes(fixedSet, descriptor)) return 'fixed'
  const isNewDefaultHidden =
    descriptor.defaultHidden && (!hasPreference || !preferenceIncludes(orderedSet, descriptor))
  if (preferenceIncludes(hiddenSet, descriptor) || isNewDefaultHidden) {
    return 'hidden'
  }
  return 'scrollable'
}

function ensureScrollableColumn(
  fixed: AssayColumnDescriptor[],
  scrollable: AssayColumnDescriptor[],
  hidden: AssayColumnDescriptor[],
): void {
  if (scrollable.length > 0) return
  if (fixed.length > 0) {
    scrollable.unshift(fixed.pop()!)
    return
  }
  if (hidden.length === 0) return
  const fallback = [...hidden].sort((left, right) => left.sourceIndex - right.sourceIndex).pop()!
  hidden.splice(hidden.indexOf(fallback), 1)
  scrollable.unshift(fallback)
}

export function reconcileAssayColumnPreference(
  preference: AssayColumnPreference | null,
  descriptors: AssayColumnDescriptor[],
): ReconciledAssayColumnLayout {
  const ordered = orderDescriptors(preference?.order ?? [], descriptors)
  const orderedSet = new Set(preference?.order ?? [])
  const fixedSet = new Set(preference?.fixed ?? [])
  const hiddenSet = new Set(preference?.hidden ?? [])
  const hasPreference = preference !== null
  const fixed: AssayColumnDescriptor[] = []
  const scrollable: AssayColumnDescriptor[] = []
  const hidden: AssayColumnDescriptor[] = []

  for (const descriptor of ordered) {
    const group = assayColumnGroup(descriptor, hasPreference, orderedSet, fixedSet, hiddenSet)
    if (!hasPreference && fixed.length === 0 && group === 'scrollable') {
      fixed.push(descriptor)
    } else if (group === 'fixed') fixed.push(descriptor)
    else if (group === 'hidden') hidden.push(descriptor)
    else scrollable.push(descriptor)
  }

  ensureScrollableColumn(fixed, scrollable, hidden)

  return {
    columns: [...fixed, ...scrollable],
    hiddenColumns: hidden,
    fixedKeys: fixed.map((descriptor) => descriptor.key),
    hiddenKeys: hidden.map((descriptor) => descriptor.key),
    fixedCount: fixed.length,
  }
}

function preferenceGroups(preference: AssayColumnPreference | null): {
  fixed: string[]
  scrollable: string[]
  hidden: string[]
} {
  const fixedSet = new Set(preference?.fixed ?? [])
  const hiddenSet = new Set(preference?.hidden ?? [])
  return {
    fixed: (preference?.order ?? []).filter((key) => fixedSet.has(key)),
    scrollable: (preference?.order ?? []).filter(
      (key) => !fixedSet.has(key) && !hiddenSet.has(key),
    ),
    hidden: (preference?.order ?? []).filter((key) => hiddenSet.has(key)),
  }
}

export function mergeVisibleAssayColumnPreference(
  current: AssayColumnPreference | null,
  descriptors: AssayColumnDescriptor[],
  visibleOrder: string[],
  visibleFixed: string[],
  visibleHidden: string[],
): AssayColumnPreference {
  const desiredOrder = [...new Set(visibleOrder)]
  const desiredKeys = new Set(desiredOrder)
  const fixedKeys = new Set(visibleFixed.filter((key) => desiredKeys.has(key)))
  const hiddenKeys = new Set(
    visibleHidden.filter((key) => desiredKeys.has(key) && !fixedKeys.has(key)),
  )
  const visiblePreferenceKeys = new Set(
    descriptors.flatMap((descriptor) => descriptorPreferenceKeys(descriptor)),
  )
  const currentGroups = preferenceGroups(current)
  const desiredFixed = desiredOrder.filter((key) => fixedKeys.has(key))
  const desiredScrollable = desiredOrder.filter(
    (key) => !fixedKeys.has(key) && !hiddenKeys.has(key),
  )
  const desiredHidden = desiredOrder.filter((key) => hiddenKeys.has(key))
  const fixed = mergePreferenceGroup(currentGroups.fixed, visiblePreferenceKeys, desiredFixed)
  const scrollable = mergePreferenceGroup(
    currentGroups.scrollable,
    visiblePreferenceKeys,
    desiredScrollable,
  )
  const hidden = mergePreferenceGroup(currentGroups.hidden, visiblePreferenceKeys, desiredHidden)
  const fixedSet = new Set(fixed)
  const normalizedScrollable = scrollable.filter((key) => !fixedSet.has(key))
  const visibleSet = new Set([...fixed, ...normalizedScrollable])
  const normalizedHidden = hidden.filter((key) => !visibleSet.has(key))

  return {
    order: [...fixed, ...normalizedScrollable, ...normalizedHidden],
    fixed,
    hidden: normalizedHidden,
  }
}

export const useAssayColumnPreferencesStore = defineStore('assayColumnPreferences', () => {
  const persistence = createColumnPreferenceStorage(
    STORAGE_PREFIX,
    parsePreference,
    clonePreference,
    'assay',
  )

  async function saveVisibleLayout(
    userId: string,
    projectId: number,
    descriptors: AssayColumnDescriptor[],
    visibleOrder: string[],
    visibleFixed: string[],
    visibleHidden: string[],
  ): Promise<AssayColumnPreferenceWriteResult> {
    return persistence.update(userId, projectId, (current) => ({
      ...mergeVisibleAssayColumnPreference(
        current,
        descriptors,
        visibleOrder,
        visibleFixed,
        visibleHidden,
      ),
      widths: { ...current?.widths },
    }))
  }

  async function saveWidth(
    userId: string,
    projectId: number,
    key: string,
    width: number,
    defaults: AssayColumnPreference,
  ): Promise<AssayColumnPreferenceWriteResult> {
    return persistence.update(userId, projectId, (current) => ({
      ...(current ?? defaults),
      widths: { ...current?.widths, [key]: Math.max(24, Math.min(5000, Math.round(width))) },
    }))
  }

  return {
    load: persistence.load,
    saveVisibleLayout,
    reset: persistence.reset,
    hydrate: persistence.hydrate,
    retry: persistence.retry,
    clear: persistence.clear,
    saveWidth,
  }
})
